<template>
  <nav
    :class="`
    ${navColor(actualUser?.role)}
    ${meta.topbarCollapse() ? ' md:fixed' : ' '}
    flex justify-center gap-3 px-4 border-gray-200 dark:bg-gray-900 sticky w-full z-50 top-0 left-0 shadow-sm`"
  >
    <div
      class="flex justify-between w-full container items-center mx-auto py-3"
    >
      <section
        id="logo-section"
        :class="`cursor-pointer max-w-fit w-12/12 sm:w-10/12 lg:w-2/12`"
      >
        <Logo />
      </section>

      <section
        id="items-logged-section"
        v-if="isLogged"
        class="hidden max-w-lg w-6/12 lg:hidden xl:flex items-end justify-center -mb-2"
      >
        <ItemsNav :role="actualUser?.role" />
      </section>

      <section id="dropdown-section" class="flex justify-end max-w-fit w-2/12">
        <div
          id="logged-items"
          v-if="isLogged"
          class="hidden sm:hidden lg:hidden xl:flex items-center"
        >
          <div class="flex gap-3 items-center w-40">
            <img
              class="rounded-full object-cover w-12 h-12 bg-gray-50"
              :src="
                actualUser?.photoProfile || require('@/assets/images/user.svg')
              "
              @error="imgDefaultError"
              alt="user"
            />
            <p
              class="btn-simple w-32 truncate cursor-pointer capitalize"
              @click="toggleMenu"
              aria-haspopup="true"
              aria-controls="overlay_menu"
              :title="actualUser?.firstName"
            >
              {{ actualUser?.firstName }}
            </p>
          </div>
          <Menu
            id="overlay_menu"
            ref="menu"
            :model="loggedDropdownItems()"
            :popup="true"
          />
        </div>
        <div
          id="not-logged-items"
          v-if="!isLogged"
          class="flex gap-4 items-center justify-end scale-90 lg:scale-100 w-full"
        >
          <ul v-for="(item, i) of unloggedItems" :key="i">
            <li>
              <Button
                :id="'btn-' + i"
                :class="`${item.isBtn ? 'btn-primary' : 'btn-simple'} truncate`"
                @click="go.redirectToPage(item.link)"
              >
                <h6 class="text-[14px] sm:text-[18px]">
                  {{ item.name }}
                </h6>
              </Button>
            </li>
          </ul>
        </div>
      </section>

      <section
        v-if="isLogged"
        id="dropdown-section-mobile"
        class="flex items-center justify-between sm:flex lg:flex xl:hidden"
        aria-controls="top-nav"
        aria-expanded="false"
        data-collapse-toggle="top-nav"
      >
        <div class="flex gap-4">
          <div class="flex gap-1 items-center w-8 h-8">
            <img
              class="rounded-full object-cover w-8 h-8 cursor-pointer"
              :src="
                actualUser?.photoProfile || require('@/assets/images/user.svg')
              "
              @error="imgDefaultError"
              alt="user"
              @click="toggleMenu"
            />
          </div>
          <Menu
            id="overlay_menu"
            ref="menu"
            :model="loggedDropdownItems()"
            :popup="true"
          />
        </div>

        <Button
          icon="pi pi-align-right"
          class="btn-simple"
          @click="showSidebar = true"
        />
      </section>
    </div>
  </nav>

  <Sidebar v-model:visible="showSidebar" :baseZIndex="10000" position="right">
    <section id="not-logged-items" v-if="!isLogged" class="flex gap-4">
      <ul v-for="(item, i) of unloggedItems" :key="i">
        <Button
          :label="item.name"
          :class="item.isBtn ? 'btn-primary' : 'btn-simple'"
          @click="go.redirectToPage(item.link)"
        />
      </ul>
    </section>

    <section v-if="isLogged">
      <div class="flex flex-col justify-center w-full gap-x-5">
        <div class="flex gap-2 items-center p-3 mb-4 text-h-primary-500">
          <v-icon name="hi-solid-user-circle" :scale="1.2" />
          <p class="font-[700]">
            {{ firstUsername(actualUser?.userName) }}
          </p>
        </div>
        <ItemsNav :role="actualUser?.role" :isMobile="true" />
      </div>
    </section>
  </Sidebar>
</template>

<script lang="ts">
import { Roles, User } from "@/models/user.model";
import { useAuthStore } from "@/store/auth";
import { GoToRoute } from "@/utils/go-to.util";
import { RouteMeta } from "@/utils/metas.util";
import { clearStorage } from "@/utils/user-store.util";
import { defineAsyncComponent, defineComponent } from "vue";

const ItemsNav = defineAsyncComponent(() => import("./Items/items-nav.vue"));

export default defineComponent({
  name: "topbar",
  computed: {
    go(): GoToRoute {
      return new GoToRoute();
    },
    meta(): RouteMeta {
      return new RouteMeta();
    },
  },
  components: { ItemsNav },
  props: {
    logo: {
      type: String,
      default: "",
    },
  },
  data() {
    const store = useAuthStore;
    return {
      showSidebar: false,
      isLogged: useAuthStore.getData !== null,
      actualUser: useAuthStore.getData as User,
      navbarItems: [] as any,
      loggedItems: [] as any[],
      unloggedItems: [] as any[],
      items: [] as any,
      routeName: this.$route.name,
      store,
    };
  },
  mounted() {
    this.actualUser = useAuthStore.getData;
    this.isLogged = this.actualUser ? true : false;
    this.loggedNavbar(this.actualUser);
    this.unloggedNavbar();
  },
  watch: {
    "store.getData": {
      handler(val) {
        this.actualUser = val;
        this.isLogged = this.actualUser ? true : false;
        this.loggedNavbar(this.actualUser);
      },
      deep: true,
    },
  },
  methods: {
    loggedNavbar(user: User): void {
      this.loggedItems = [
        {
          name: this.firstUsername(user?.userName),
          link: "sign-in",
          isBtn: false,
        },
      ];
    },
    loggedDropdownItems(): Array<any> {
      return [
        {
          items: [
            {
              label: this.$t("labels.generic", { pt: "Conta", en: "Account" }),
              name: "editUser",
              icon: "pi pi-users",
              command: () => this.go.redirectToPage("editUser"),
            },
            ...this.admDropdown(),
            ...this.teacherDropdown(),
            ...this.studentDropdown(),
            {
              label: this.$t("labels.generic", {
                pt: "Sobre a plataforma",
                en: "About the platform",
              }),
              name: "about",
              icon: "pi pi-info-circle",
              command: () => this.go.redirectToPage("about"),
            },
            {
              label: this.$t("labels.generic", { pt: "Sair", en: "Logout" }),
              icon: "pi pi-power-off",
              command: () => this.logoutUser(),
            },
          ],
        },
      ];
    },
    admDropdown(): Array<any> {
      const isAdm = this.actualUser?.role?.includes(Roles.APPLICATION_ADMIN);
      return isAdm
        ? [
            {
              label: this.$t("labels.generic", {
                pt: "Configurações gerais",
                en: "General settings",
              }),
              name: "generalSettings",
              icon: "pi pi-cog",
              command: () => this.go.redirectToPage("generalSettings"),
            },
          ]
        : [];
    },
    teacherDropdown(): Array<any> {
      const isTeacher = this.actualUser?.role?.includes(Roles.TEACHER);
      return isTeacher ? [] : [];
    },
    studentDropdown(): Array<any> {
      const isStd = this.actualUser?.role?.includes(Roles.STUDENT);
      return isStd ? [] : [];
    },
    unloggedNavbar(): void {
      this.unloggedItems = [
        {
          name: "Login",
          link: "sign-in",
          isBtn: false,
        },
        {
          name: this.$t("labels.generic", {
            pt: "Cadastre-se",
            en: "Sign up",
          }),
          link: "sign-up",
          isBtn: true,
        },
      ];
    },
    firstUsername(username?: string): string {
      var arrName = username?.split(" ");
      return arrName ? arrName[0] : "";
    },
    logoutUser(): void {
      clearStorage();
    },
    toggleMenu(event: any): void {
      (this.$refs.menu as any).toggle(event);
    },
    navColor(value: string): string {
      if (value == Roles.STUDENT) {
        value = "bg-h-primary-50";
      } else {
        value = "bg-white";
      }
      return value;
    },
    imgDefaultError(e: any): void {
      e.target.src =
        "https://video-thumb.s3.sa-east-1.amazonaws.com/65197bdbplaceholder.png";
    },
  },
});
</script>
